import React, { useState } from 'react';
import DocumentPreview from '../DocumentPreview';
import './DetailsSection.css';

export default function CourseDetailsSection({
  course,
  courseInfo,
  eventsCount,
  assignmentsCount,
  syllabusDocId,
  syllabusDocName,
  onEdit
}) {
  const [showSyllabusPreview, setShowSyllabusPreview] = useState(false);
  
  console.log('[CourseDetailsSection] Rendered with:', { 
    courseId: course?.id, 
    syllabusDocId, 
    syllabusDocName,
    showSyllabusPreview 
  });
  
  if (!course) return null;

  const getMeta = (key) => {
    const raw = Array.isArray(course?.metadata) ? course.metadata : [];
    const item = raw.find(m => (m?.metadataName || '').trim().toLowerCase() === key.toLowerCase());
    return (item?.value || '').toString().trim() || null;
  };

  const courseLocation = getMeta('course location');
  const semester = getMeta('semester');
  const meetingTimes = getMeta('meeting times');
  const officeHours = getMeta('office hours');
  const courseWebsite = getMeta('course website');

  // Normalize website to a valid absolute URL without using hooks (fix rules-of-hooks)
  const normalizedWebsite = (() => {
    const raw = (courseWebsite || '').trim();
    if (!raw) return null;
    const url = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    try {
      new URL(url);
      return url;
    } catch {
      return null;
    }
  })();

  const instructorEmails = getMeta('instructor emails');
  const instructorPhone = getMeta('instructor phone');
  const meetingStart = getMeta('meeting start time');
  const meetingEnd = getMeta('meeting end time');
  const schedulePattern = getMeta('schedule pattern');

  const formatMeta = (item) => {
    if (item?.metadataFieldType?.toLowerCase() === 'date' && item?.value) {
      try {
        const d = item.value.includes('T') ? new Date(item.value) : new Date(`${item.value}T00:00:00`);
        if (!isNaN(d)) {
          return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        }
      } catch {}
    }
    return item?.value ?? 'Not set';
  };

  const rawMetadata = Array.isArray(course.metadata) ? course.metadata : [];

  // Exclude keys we render explicitly below
  const EXCLUDED_NAMES = new Set([
    'name', 'course name', 'title',
    'description', 'course description',
    'start date', 'end date', 'duration',
    'created', 'updated',
    'total events', 'total assignments',
    'course location',
    'semester',
    'meeting times',
    'meeting start time',
    'meeting end time',
    'schedule pattern',
    'office hours',
    'course website',
    'instructor emails',
    'instructor phone'
  ]);

  const dedupedMetaMap = rawMetadata.reduce((map, item) => {
    const key = (item?.metadataName || '').trim().toLowerCase();
    if (!key || EXCLUDED_NAMES.has(key)) return map;
    const prev = map.get(key);
    const score = (d) => {
      const t = new Date(d?.updatedAt || d?.createdAt || 0).getTime();
      return Number.isFinite(t) ? t : (d?.id || 0);
    };
    if (!prev || score(item) >= score(prev)) map.set(key, item);
    return map;
  }, new Map());

  const metadataItems = Array.from(dedupedMetaMap.values());

  return (
    <div className="course-combined">
      <div className="course-top-row">
        <div className="course-icon-large">🎓</div>
        <div className="course-title-meta">
          <h1 className="course-title">{course.name}</h1>
          <div className="course-meta-pills">
            <span className="course-type-pill">{courseInfo.typeName}</span>
            <span className="course-stats-pill">{eventsCount} {eventsCount === 1 ? 'Event' : 'Events'}</span>
            <span className="course-stats-pill">{assignmentsCount} {assignmentsCount === 1 ? 'Assignment' : 'Assignments'}</span>
            {courseLocation && <span className="location-badge" title="Course Location">🏫 {courseLocation}</span>}
            {semester && <span className="location-badge" title="Semester">📅 {semester}</span>}
          </div>
        </div>
        <div className="course-actions">
          {syllabusDocId && (
            <button
              className="btn btn-secondary view-syllabus-btn"
              onClick={() => setShowSyllabusPreview(!showSyllabusPreview)}
            >
              {showSyllabusPreview ? '📕 Hide Syllabus' : '📘 View Syllabus'}
            </button>
          )}
          <button className="btn btn-primary edit-course-btn mobile-hide" onClick={onEdit}>✏️ Edit Course</button>
        </div>
      </div>

      {course.description && (
        <div className="course-description-block">{course.description}</div>
      )}

      <div className="course-details-grid">
        <div className="detail-item"><label>Start Date</label><p>{courseInfo.startDate}</p></div>
        <div className="detail-item"><label>End Date</label><p>{courseInfo.endDate}</p></div>
        <div className="detail-item"><label>Duration</label><p>{courseInfo.duration}</p></div>
        {courseLocation && (
          <div className="detail-item"><label>Course Location</label><p>🏫 {courseLocation}</p></div>
        )}
        {semester && (
          <div className="detail-item"><label>Semester</label><p>{semester}</p></div>
        )}
        {meetingTimes && (
          <div className="detail-item"><label>Meeting Times</label><p>{meetingTimes}</p></div>
        )}
        {(meetingStart || meetingEnd) && (
          <div className="detail-item"><label>Meeting Window</label><p>{meetingStart || '—'} – {meetingEnd || '—'}</p></div>
        )}
        {schedulePattern && (
          <div className="detail-item"><label>Schedule Pattern</label><p>{schedulePattern}</p></div>
        )}
        {officeHours && (
          <div className="detail-item"><label>Office Hours</label><p>{officeHours}</p></div>
        )}
        {normalizedWebsite ? (
          <div className="detail-item">
            <label>Website</label>
            <p>
              <a href={normalizedWebsite} target="_blank" rel="noopener noreferrer">
                {courseWebsite}
              </a>
            </p>
          </div>
        ) : (
          courseWebsite && (
            <div className="detail-item">
              <label>Website</label>
              <p>{courseWebsite}</p>
            </div>
          )
        )}
        {instructorEmails && (
          <div className="detail-item"><label>Instructor Emails</label><p>{instructorEmails}</p></div>
        )}
        {instructorPhone && (
          <div className="detail-item"><label>Instructor Phone</label><p>{instructorPhone}</p></div>
        )}
        <div className="detail-item"><label>Created</label><p>{courseInfo.createdAt}</p></div>
        <div className="detail-item"><label>Updated</label><p>{courseInfo.updatedAt}</p></div>
        <div className="detail-item"><label>Total Events</label><p>{eventsCount}</p></div>
        <div className="detail-item"><label>Total Assignments</label><p>{assignmentsCount}</p></div>

        {metadataItems.map((m) => (
          <div key={m.id} className="detail-item">
            <label>{m.metadataName}</label>
            <p title={String(m.value ?? '')}>{formatMeta(m)}</p>
          </div>
        ))}
      </div>

      {syllabusDocId && (
        <>
          {!showSyllabusPreview && (
            <div className="syllabus-toggle-section">
              <button 
                className="btn btn-primary" 
                onClick={() => setShowSyllabusPreview(true)}
                title="View syllabus document inline"
              >
                📘 View Syllabus
              </button>
            </div>
          )}

          {showSyllabusPreview && (
            <div className="course-syllabus-preview-section">
              <div className="syllabus-preview-header">
                <h3>📘 Syllabus</h3>
                <button 
                  className="btn btn-ghost" 
                  onClick={() => setShowSyllabusPreview(false)}
                  title="Hide syllabus preview"
                >
                  ✕ Hide
                </button>
              </div>
              <DocumentPreview 
                documentId={syllabusDocId}
                documentName={syllabusDocName || 'Syllabus'}
                className="course-view-syllabus-preview"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}